import { getActiveAdventure } from "../adventure/ActiveAdventure.js";
import type {
  ContentCategory,
  SRDCategoryMap,
  SRDMonster,
  SRDItem,
  SRDSpell,
  SRDClass,
  SRDRace,
  SRDBackground,
} from "../types/srd.js";

// ── Overlay type ─────────────────────────────────────────────────────────────

/**
 * Per-adventure data that patches or extends the SRD baseline.
 * Each entry is a partial record merged on top of the SRD value, or a full
 * standalone record for content that doesn't exist in the SRD at all.
 * In v1 this layer is always empty; platformification mounts it at boot.
 */
export interface AdventureOverlay {
  monsters?: Record<string, Partial<SRDMonster>>;
  items?: Record<string, Partial<SRDItem>>;
  spells?: Record<string, Partial<SRDSpell>>;
  classes?: Record<string, Partial<SRDClass>>;
  races?: Record<string, Partial<SRDRace>>;
  backgrounds?: Record<string, Partial<SRDBackground>>;
}

// ── DataRegistry ─────────────────────────────────────────────────────────────

/**
 * Single source of truth for all authored content.
 *
 * Fetch flow for a given category + id:
 *   1. Check the in-memory cache.
 *   2. Fetch the adventure-scoped baseline from
 *      `<adventureBase>/data/{category}/{id}.json` (null on 404).
 *   3. If an adventure overlay has a matching entry, shallow-merge it on top.
 *   4. Cache and return the merged record.
 *
 * Callers must never touch fetch() directly — always use this
 * registry so the overlay layer is always applied.
 */
export class DataRegistry {
  private readonly cache = new Map<string, SRDCategoryMap[ContentCategory] | null>();
  private overlay: AdventureOverlay = {};

  /**
   * Mount a per-adventure overlay. Call once at boot before any get*().
   * Calling again replaces the previous overlay and clears the cache so
   * previously fetched records are re-merged.
   */
  mount(overlay: AdventureOverlay): void {
    this.overlay = overlay;
    this.cache.clear();
  }

  /** Fetch a monster record with the overlay applied. Returns null if not found. */
  async getMonster(id: string): Promise<SRDMonster | null> {
    return this.get("monsters", id) as Promise<SRDMonster | null>;
  }

  /** Fetch an item record with the overlay applied. Returns null if not found. */
  async getItem(id: string): Promise<SRDItem | null> {
    return this.get("items", id) as Promise<SRDItem | null>;
  }

  /** Fetch a spell record with the overlay applied. Returns null if not found. */
  async getSpell(id: string): Promise<SRDSpell | null> {
    return this.get("spells", id) as Promise<SRDSpell | null>;
  }

  /** Fetch a class record with the overlay applied. Returns null if not found. */
  async getClass(id: string): Promise<SRDClass | null> {
    return this.get("classes", id) as Promise<SRDClass | null>;
  }

  /** Fetch a race record with the overlay applied. Returns null if not found. */
  async getRace(id: string): Promise<SRDRace | null> {
    return this.get("races", id) as Promise<SRDRace | null>;
  }

  /** Fetch a background record with the overlay applied. Returns null if not found. */
  async getBackground(id: string): Promise<SRDBackground | null> {
    return this.get("backgrounds", id) as Promise<SRDBackground | null>;
  }

  /**
   * Generic fetch by category. Returns the merged record or null.
   * Prefer the typed helpers above; this is for dynamic/generic callers.
   */
  async get<C extends ContentCategory>(
    category: C,
    id: string,
  ): Promise<SRDCategoryMap[C] | null> {
    const cacheKey = `${category}/${id}`;
    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey) as SRDCategoryMap[C] | null;
    }

    const baseline = await this.fetchBaseline<C>(category, id);
    const patch = (this.overlay[category] as Record<string, Partial<SRDCategoryMap[C]>> | undefined)?.[id];

    const merged =
      baseline === null && patch === undefined
        ? null
        : ({ ...(baseline ?? {}), ...(patch ?? {}) } as SRDCategoryMap[C]);

    this.cache.set(cacheKey, merged);
    return merged;
  }

  private async fetchBaseline<C extends ContentCategory>(
    category: C,
    id: string,
  ): Promise<SRDCategoryMap[C] | null> {
    const path = `${getActiveAdventure().baseUrl}/data/${category}/${id}.json`;
    let response: Response;
    try {
      response = await fetch(path);
    } catch {
      return null;
    }
    if (!response.ok) return null;
    // Vite's dev server (and most SPA hosts) returns the index.html shell
    // with a 200 OK for unknown paths. response.json() would then throw on
    // "<!doctype ...". Treat any non-JSON content-type as "missing" so
    // spawn-time fallbacks fire instead of bringing the whole load down.
    const contentType = response.headers.get("content-type") ?? "";
    if (!contentType.includes("application/json")) return null;
    try {
      return (await response.json()) as SRDCategoryMap[C];
    } catch {
      return null;
    }
  }
}

/** Singleton registry — the only place engine code loads authored content. */
export const dataRegistry = new DataRegistry();
